import type { ThoughtContent } from '@/features/thought/lib/thought'

/** Backend MediaType enum: 0 unknown, 10 image, 20 video, 30 thought (a text card). */
export const MEDIA_TYPE = { unknown: 0, image: 10, video: 20, thought: 30 } as const

/** Feed post DTO after snake_case → camelCase conversion. */
export interface FeedPost {
  id: string
  createdAt: string
  userId: string | null
  username: string
  /** Denormalized author display name - preferred over the username. */
  displayName?: string | null
  avatarUrl: string
  mediaType: number
  textContent: string | null
  /** Mood enum name ("SciFi"), numeric string ("13") or number (13), depending on endpoint. */
  mood: string | number | null
  likeCount: number
  commentCount: number
  sharesCount: number
  viewCount: number
  repostCount: number
  giftsCount: number
  pageId: string | null
  pageUsername: string | null
  pageName: string | null
  pageAvatarUrl: string | null
  /** Posted into a community - identity/routing goes to the community. */
  communityId?: string | null
  communitySlug?: string | null
  communityName?: string | null
  communityAvatarUrl?: string | null
  title: string | null
  isAiGenerated: boolean
  isCommercial: boolean
  linkUrl: string | null
  isRepost: boolean
  /** Author's reuse switch - off hides the repost action for others. */
  allowReuse?: boolean | null
  originalCreatorUsername: string | null
  width: number | null
  height: number | null
  /** JSON-encoded array string, or a bare URL for some video posts. */
  mediaUrls: string | null
  thumbnailUrl: string | null
  /** The card of a Thought post; null for pictures and videos. */
  thought?: ThoughtContent | null
  /** Library sound linked to the post: baked into artwork videos, streamed under a Thought card. */
  soundId?: string | null
  soundUrl?: string | null
  soundName?: string | null
  isFollowing: boolean
  isLiked?: boolean | null
  isSaved?: boolean | null
  isShared?: boolean | null
  isGifted?: boolean | null
  isCommented?: boolean | null
  /** Server-stamped on every successful edit - drives the "edited" pill. */
  editedAt?: string | null
  allowComments?: boolean | null
  showPostDate?: boolean | null
  /** PostVisibility: 10 public, 20 followers, 30 private, 40 community. */
  visibility?: number | null
  /** UserNature bit flags of the author: 1 = Pioneer, 2 = Premium. */
  userNature?: number | null
  /** Admin marked as fake news - FAKE stamp; creator can't delete it. */
  isFakeNews?: boolean | null
  /** Admin blocked from the feed - only the owner sees it (BLOCKED stamp). */
  isBlocked?: boolean | null
}

export interface Comment {
  id: string
  text: string
  createdAt: string
  likesCount: number
  isLiked: boolean
  imageUrl?: string | null
  thumbnailUrl?: string | null
  /** Comment author's user id (wire: author_id) - target of a report. */
  authorId?: string | null
  /** Viewer wrote this comment (wire: is_own) - hides the report action. */
  isOwn?: boolean | null
  authorUsername?: string | null
  username?: string | null
  authorAvatarUrl?: string | null
  avatarUrl?: string | null
}

export interface Gift {
  id: string
  /** Backend GiftType enum value (1 Clap … 10 Rocket). */
  type?: number | null
  /** GiftType enum name (e.g. "Clap", "Wheelbarrow"). */
  name: string
  priceCoins?: number | null
  animationUrl?: string | null
}

export interface PostGift {
  id: string
  giftType?: number | null
  giftName: string
  giftAnimationUrl?: string | null
  senderName: string
  senderAvatar?: string | null
  isAnonymous: boolean
  count: number
}

export interface FeedSearchUser {
  id: string
  name: string
  avatar?: string | null
}

export interface FeedSearchPost {
  id: string
  description?: string | null
  likes: number
  views: number
  shares: number
  gifts: number
  comments: number
  avatar?: string | null
}

export interface FeedSearchResponse {
  users?: FeedSearchUser[] | null
  posts?: FeedSearchPost[] | null
}

// ---- Derived helpers (mirror the iOS FeedPost extensions) ----

/** Posted to a page: identity shows the page, avatar routes to the page. */
export function isPagePost(post: FeedPost): boolean {
  return post.pageId != null && post.pageUsername != null
}

/** Posted into a community: the avatar/name route to the community page. */
export function isCommunityPost(post: FeedPost): boolean {
  return post.communityId != null && post.communitySlug != null
}

export function displayUsername(post: FeedPost): string {
  return isPagePost(post) ? (post.pageName ?? post.pageUsername ?? post.username) : post.username
}

/** Identity line: page name → author display name → username. */
export function displayTitle(post: FeedPost): string {
  if (isPagePost(post)) return post.pageName ?? post.pageUsername ?? post.username
  return post.displayName || post.username
}

/** The @handle under the display title: page handle for page posts. */
export function displayHandle(post: FeedPost): string {
  return isPagePost(post) ? (post.pageUsername ?? post.username) : post.username
}

export function displayAvatarUrl(post: FeedPost): string | null {
  return isPagePost(post) ? (post.pageAvatarUrl ?? post.avatarUrl) : post.avatarUrl
}

/** `mediaUrls` arrives as a JSON-array string; some posts send a bare URL. */
export function mediaUrlList(post: { mediaUrls: string | null }): string[] {
  const raw = post.mediaUrls
  if (!raw) return []
  try {
    const decoded: unknown = JSON.parse(raw)
    if (Array.isArray(decoded)) return decoded.filter((item): item is string => !!item)
  } catch {
    /* bare URL */
  }
  return [raw]
}

export function commentUsername(comment: Comment): string {
  return comment.authorUsername ?? comment.username ?? 'Unknown'
}

export function commentAvatarUrl(comment: Comment): string | null {
  return comment.authorAvatarUrl ?? comment.avatarUrl ?? null
}

export function commentImageUrl(comment: Comment): string | null {
  return comment.thumbnailUrl ?? comment.imageUrl ?? null
}

/**
 * Bundled gift art served from public/gifts/, mapped from the catalog's
 * animation_url filename (e.g. "gifts/clapping.gif" → "/gifts/clapping.gif").
 */
export function giftImageSrc(animationUrl: string | null | undefined): string {
  const fileName = animationUrl?.split('/').pop()
  return `/gifts/${fileName || 'heart.gif'}`
}
